import React from 'react';
import { useTranslation } from 'react-i18next';

import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { TransferPanel } from '@/components/ui/panels/TransferPanel';
import { PermissionGuard } from '@/components/layout';
import { useAssignLocationTab } from '@/hooks/checkInLocation';


export const AssignLocationTab: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    locationOptions,
    selectedLocationId,
    onLocationChange,
    available,
    filteredAvailable,
    isAvailableLoading,
    hasMoreAvailable,
    totalAvailable,
    availableSearchText,
    onAvailableSearch,
    availableSentinelRef,
    availableScrollContainerRef,
    assigned,
    filteredAssigned,
    isAssignedLoading,
    hasMoreAssigned,
    totalAssigned,
    assignedSearchText,
    onAssignedSearch,
    assignedSentinelRef,
    assignedScrollContainerRef,
    selectedAvailableIds,
    onToggleAvailable,
    onSelectAllAvailable,
    isAllAvailableSelected,
    isAvailableIndeterminate,
    selectedAssignedIds,
    onToggleAssigned,
    onSelectAllAssigned,
    isAllAssignedSelected,
    isAssignedIndeterminate,
    onAdd,
    onRemove,
    onDoubleClickAvailable,
    onDoubleClickAssigned,
    onAddAllFromServer,
    onRemoveAllFromServer,
    isAddAllPending,
    isRemoveAllPending,
    isDirty,
    isSaving,
    canEdit,
    onSave,
    onCancel,
  } = useAssignLocationTab();

  return (
    <div className="flex flex-col gap-6" data-testid="assign-location-tab">
      <div className="w-96">
        <Selector
          label={translate('checkInLocation:assignLocation.locationLabel')}
          placeholder={translate('checkInLocation:assignLocation.locationPlaceholder')}
          options={locationOptions}
          value={selectedLocationId ? String(selectedLocationId) : undefined}
          onChange={(value) => onLocationChange(value ? Number(value) : null)}
          data-testid="assign-location-selector"
        />
      </div>

      <TransferPanel
        keyExtractor={(employee) => employee.employeeId}
        renderTitle={(employee) => employee.employeeName}
        renderSubtitle={(employee) => employee.employeeTaxIdentificationNumber}
        leftPanel={{
          title: translate('checkInLocation:assignLocation.availableTitle'),
          items: filteredAvailable,
          allItems: available,
          isLoading: isAvailableLoading,
          totalRecords: totalAvailable,
          searchValue: availableSearchText,
          onSearch: onAvailableSearch,
          searchPlaceholder: translate('checkInLocation:assignLocation.search'),
          selectedIds: selectedAvailableIds,
          onToggleSelect: onToggleAvailable,
          onSelectAll: onSelectAllAvailable,
          isAllSelected: isAllAvailableSelected,
          isIndeterminate: isAvailableIndeterminate,
          onDoubleClick: canEdit ? onDoubleClickAvailable : () => {},
          onBulkAction: (isRemoveAllPending || !canEdit) ? undefined : onAddAllFromServer,
          bulkActionMessage: translate(
            'checkInLocation:assignLocation.addAllBannerMessage',
            { total: totalAvailable },
          ),
          sentinelRef: availableSentinelRef,
          scrollContainerRef: availableScrollContainerRef,
          selectAllHint: hasMoreAvailable
            ? translate('checkInLocation:assignLocation.selectAllHint', { loaded: available.length })
            : undefined,
          footerText: (isRemoveAllPending && hasMoreAvailable)
            ? translate('checkInLocation:assignLocation.footerSavePending', {
              pending: totalAvailable - available.length,
            })
            : hasMoreAvailable
              ? translate('checkInLocation:assignLocation.footerPartial', {
                selected: selectedAvailableIds.size,
                loaded: available.length,
                total: totalAvailable,
              })
              : translate('checkInLocation:assignLocation.footer', {
                selected: selectedAvailableIds.size,
                total: available.length,
              }),
          'data-testid': 'available-employees-panel',
        }}
        rightPanel={{
          title: translate('checkInLocation:assignLocation.assignedTitle'),
          items: filteredAssigned,
          allItems: assigned,
          isLoading: isAssignedLoading,
          totalRecords: totalAssigned,
          searchValue: assignedSearchText,
          onSearch: onAssignedSearch,
          searchPlaceholder: translate('checkInLocation:assignLocation.search'),
          selectedIds: selectedAssignedIds,
          onToggleSelect: onToggleAssigned,
          onSelectAll: onSelectAllAssigned,
          isAllSelected: isAllAssignedSelected,
          isIndeterminate: isAssignedIndeterminate,
          onDoubleClick: canEdit ? onDoubleClickAssigned : () => {},
          onBulkAction: (isAddAllPending || !canEdit) ? undefined : onRemoveAllFromServer,
          bulkActionMessage: translate(
            'checkInLocation:assignLocation.removeAllBannerMessage',
            { total: totalAssigned },
          ),
          sentinelRef: assignedSentinelRef,
          scrollContainerRef: assignedScrollContainerRef,
          selectAllHint: hasMoreAssigned
            ? translate('checkInLocation:assignLocation.selectAllHint', { loaded: assigned.length })
            : undefined,
          footerText: (isAddAllPending && hasMoreAssigned)
            ? translate('checkInLocation:assignLocation.footerSavePending', {
              pending: totalAssigned - assigned.length,
            })
            : hasMoreAssigned
              ? translate('checkInLocation:assignLocation.footerPartial', {
                selected: selectedAssignedIds.size,
                loaded: assigned.length,
                total: totalAssigned,
              })
              : translate('checkInLocation:assignLocation.footer', {
                selected: selectedAssignedIds.size,
                total: assigned.length,
              }),
          'data-testid': 'assigned-employees-panel',
        }}
        onAdd={onAdd}
        onRemove={onRemove}
        addLabel={translate('checkInLocation:assignLocation.addButton')}
        removeLabel={translate('checkInLocation:assignLocation.removeButton')}
        addAllLabel={translate('checkInLocation:assignLocation.addAll')}
        removeAllLabel={translate('checkInLocation:assignLocation.removeAll')}
        isAddDisabled={selectedAvailableIds.size === 0 || !canEdit}
        isRemoveDisabled={selectedAssignedIds.size === 0 || !canEdit}
        data-testid="assign-location-transfer"
      />

      <div className="flex justify-center gap-3 pt-2">
        <PrimaryButton
          variant="outline"
          onClick={onCancel}
          disabled={!isDirty || isSaving}
          data-testid="assign-location-cancel-button"
        >
          {translate('checkInLocation:assignLocation.cancel')}
        </PrimaryButton>
        <PermissionGuard action="edit">
          <PrimaryButton
            onClick={() => { void onSave(); }}
            isLoading={isSaving}
            disabled={!isDirty || isSaving}
            data-testid="assign-location-save-button"
          >
            {translate('checkInLocation:assignLocation.saveChanges')}
          </PrimaryButton>
        </PermissionGuard>
      </div>
    </div>
  );
};

export default AssignLocationTab;
